import type { AuditEventItem } from "@/hooks/useAuditEvents";

export interface GateDefectCount {
  gate: string;
  count: number;
}

export interface GateDefectsSummary {
  total: number;
  byGate: GateDefectCount[];
}

/**
 * Count defects caught by gates before merge.
 *
 * A defect is a failed `gate_decision`. Repeated failures of the same gate on
 * the same work item (retries) collapse into one defect: dedup key is
 * (work_item_ref, subject_id). Events without a work item ref are keyed by
 * their own id so they are never merged with each other.
 */
export function aggregateGateDefects(events: AuditEventItem[]): GateDefectsSummary {
  const seen = new Set<string>();
  const counts = new Map<string, number>();

  for (const e of events) {
    if (e.event_kind !== "gate_decision" || e.decision !== "fail") continue;
    const key = `${e.work_item_ref ?? `#${e.id}`}::${e.subject_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    counts.set(e.subject_id, (counts.get(e.subject_id) ?? 0) + 1);
  }

  const byGate = Array.from(counts.entries())
    .map(([gate, count]) => ({ gate, count }))
    .sort((a, b) => b.count - a.count || a.gate.localeCompare(b.gate));

  return { total: seen.size, byGate };
}
